import type { ComponentPropsWithoutRef, ReactNode, JSX, ElementType } from 'react';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { isNotNil, isRecord, isNotUndefined } from '@wistia/type-guards';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import type { Spacings } from '../../css/designTokens/spacing';
import type {
  JustifyContentType,
  AlignContentType,
  AlignItemsType,
  AlignSelfType,
  DirectionType,
} from './flexTypes';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';
import { makePolymorphic } from '../../private/helpers/makePolymorphic';

type FillType = boolean | 'horizontal' | 'vertical';

type Gap = {
  column: Spacings;
  row: Spacings;
};

type ResponsiveGap = ResponsiveObject<Gap | Spacings>;

export type FlexModeType = 'equal' | 'fluid' | 'grow' | 'initial' | 'rigid';

const getGapStyle = (gap?: Gap | Spacings) => {
  if (isNotNil(gap)) {
    if (isRecord(gap)) {
      return Object.entries(gap)
        .map(([key, value]) => {
          return `${key}-gap: var(--wui-${value as string});`;
        })
        .join('');
    }
    return `gap: var(--wui-${gap});`;
  }
  return undefined;
};

const getFillStyle = (fill: FillType): string | undefined => {
  if (fill === 'horizontal') {
    return 'width: 100%;';
  }
  if (fill === 'vertical') {
    return 'height: 100%;';
  }
  if (fill) {
    return `
      width: 100%;
      height: 100%;
    `;
  }
  return undefined;
};

const getFillViewportStyle = (
  fillViewport: boolean | 'horizontal' | 'vertical',
): string | undefined => {
  if (fillViewport === 'horizontal') {
    return 'width: 100vw;';
  }
  if (fillViewport === 'vertical') {
    return 'height: 100vh;';
  }
  if (fillViewport) {
    return `
      width: 100vw;
      height: 100vh;
    `;
  }
  return undefined;
};

const getFlexStyle = (flexMode?: FlexModeType) => {
  if (!flexMode) {
    return null;
  }

  switch (flexMode) {
    // grows to fill space, won't shrink, starts at 0
    case 'grow':
      return css`
        flex: 1 0 0;
      `;

    // default behavior, can shrink but won't grow
    case 'initial':
      return css`
        flex: 0 1 auto;
      `;

    // equal distribution of space
    case 'equal':
      return css`
        flex: 1;
      `;

    // won't grow or shrink
    case 'rigid':
      return css`
        flex: 0 0 auto;
      `;

    // can grow and shrink from content size
    case 'fluid':
      return css`
        flex: 1 1 auto;
      `;

    default:
      return null;
  }
};

type StyledBoxComponentProps = {
  $alignContent: AlignContentType;
  $alignItems: AlignItemsType;
  $alignSelf?: AlignSelfType | undefined;
  $basis?: string | undefined;
  $fillBox: FillType;
  $fillViewport: FillType;
  $flexDirection: DirectionType;
  $flexMode?: FlexModeType;
  $gap?: Gap | Spacings | undefined;
  $grow?: number | string | undefined;
  $height?: string | undefined;
  $inline?: boolean;
  $justifyContent: JustifyContentType;
  $order?: number | 'inherit' | 'initial' | 'unset' | undefined;
  $shrink?: number | undefined;
  $width?: string | undefined;
  $wrapItems: boolean;
};

const StyledBoxComponent = styled.div<StyledBoxComponentProps>`
  /* properties meant for Box parent (flex container) */
  align-content: ${({ $alignContent }) => $alignContent};
  align-items: ${({ $alignItems }) => $alignItems};
  display: ${({ $inline }) => (isNotNil($inline) && $inline ? 'inline-flex' : 'flex')};
  flex-direction: ${({ $flexDirection }) => $flexDirection};
  flex-wrap: ${({ $wrapItems }) => ($wrapItems ? 'wrap' : 'nowrap')};
  justify-content: ${({ $justifyContent }) => $justifyContent};
  height: ${({ $height }) => $height ?? null};
  width: ${({ $width }) => $width ?? null};
  ${({ $fillBox: fill }) => getFillStyle(fill)};
  ${({ $fillViewport }) => getFillViewportStyle($fillViewport)};
  ${({ $flexMode }) => getFlexStyle($flexMode)};
  ${({ $gap }) => getGapStyle($gap)};

  /* properties meant for Box children (flex items) */
  align-self: ${({ $alignSelf }) => $alignSelf ?? null};
  flex-basis: ${({ $basis }) => (isNotNil($basis) ? $basis : null)};
  flex-grow: ${({ $grow }) => (isNotNil($grow) ? $grow : null)};
  flex-shrink: ${({ $shrink }) => (isNotNil($shrink) ? $shrink : null)};
  order: ${({ $order }) => (isNotNil($order) ? $order : null)};
`;

const DEFAULT_ELEMENT = 'div';

/**
 * CSS property => Box prop:
 * flex-direction => direction
 * justify-content => justifyContent
 * align-items => alignItems
 * align-content => alignContent
 * flex-wrap => wrapItems
 * gap => gap
 * display: inline-flex => inline
 */
export type BoxProps = ComponentPropsWithoutRef<typeof DEFAULT_ELEMENT> & {
  /**
   * Specifies how the browser distributes space between and around
   * items along the cross-axis of their container; has no effect on
   * single line flexible boxes. Can be a [responsive prop](/docs/responsive-props).
   * @type ResponsiveObject<"center" | "flex-end" | "flex-start" | "space-around" | "space-between" | "stretch">
   */
  alignContent?: AlignContentType | ResponsiveObject<AlignContentType>;
  /**
   * Specifies how the browser distributes space between and around
   * items along the cross-axis of their container; the difference
   * to the alignContent prop is that alignItems specifies the
   * alignment of the items within the current container's line,
   * whereas alignContent specifies the alignment of the lines themselves.
   * Can be a [responsive prop](/docs/responsive-props).
   * @type ResponsiveObject<"baseline" | "center" | "flex-end" | "flex-start" | "stretch">
   */
  alignItems?: AlignItemsType | ResponsiveObject<AlignItemsType>;
  /**
   * Specifies how the browser aligns items of the current line, overriding the
   * alignItems value of parent; if any of the item's cross-axis margin is set to auto,
   * then alignSelf is ignored. Can be a [responsive prop](/docs/responsive-props).
   *
   * *Note: only works for nested `<Box />`*
   *
   * @type ResponsiveObject<'auto' | 'baseline' | 'center' | 'flex-end' | 'flex-start' | 'stretch'>
   */
  alignSelf?: AlignSelfType | ResponsiveObject<AlignSelfType>;
  /**
   * Specifies the initial main size of an item. Can be a [responsive prop](/docs/responsive-props).
   *
   * *Note: Cannot be used with `flexMode` prop.*
   *
   * *Note: only works for nested `<Box />`*
   * @type ResponsiveObject<'string'>

   */
  basis?: ResponsiveObject<string> | string;
  /**
   * Pass an arbitrary child node
   */
  children?: ReactNode;
  /**
   * Specifies the main axis of the container. Can be a [responsive prop](/docs/responsive-props).
   * @type ResponsiveObject<'column-reverse' | 'column' | 'row-reverse' | 'row'>
   */
  direction?: DirectionType | ResponsiveObject<DirectionType>;
  /**
   * Specifies whether the width and/or height should fill the container.
   * Can be a [responsive prop](/docs/responsive-props).
   *
   * *Note: Cannot be used with height/width props when setting the corresponding dimension.*
   *
   * For example, `fill="vertical"` or `fill={true}` cannot be used with the `height` prop,
   * and `fill="horizontal"` or `fill={true}` cannot be used with the `width` prop.
   * @type ResponsiveObject<boolean | 'horizontal' | 'vertical'>
   */
  fill?: FillType | ResponsiveObject<FillType>;
  /**
   * Specifies whether the width and/or height should fill the viewport.
   *
   * *Note: Cannot be used with height/width props when setting the corresponding dimension.*
   *
   * For example, `fillViewport="vertical"` or `fillViewport={true}` cannot be used with the `height` prop,
   * and `fillViewport="horizontal"` or `fillViewport={true}` cannot be used with the `width` prop.
   * @type boolean | 'horizontal' | 'vertical'
   */
  fillViewport?: FillType;
  /**
   * Predefined `flex` behavior patterns. Can be a [responsive prop](/docs/responsive-props).
   *
   * - `grow`: fills available space (`flex: 1 0 0`)
   * - `initial`: default behavior (`flex: 0 1 auto`)
   * - `equal`: equal space distribution (`flex: 1`)
   * - `rigid`: fixed size (`flex: 0 0 auto`)
   * - `fluid`: responsive (`flex: 1 1 auto`)
   *
   * @type ResponsiveObject<'equal' | 'fluid' | 'grow' | 'initial' | 'rigid'>
   */
  flexMode?: FlexModeType | ResponsiveObject<FlexModeType>;
  /**
   * Specifies the gaps (gutters) between rows and columns.
   * Can be a [responsive prop](/docs/responsive-props).
   *
   * Can be either a single spacing value that applies to both row and column gaps,
   * or an object specifying different values for row and column gaps.
   *
   * @type ResponsiveObject<Spacings | { row: Spacings; column: Spacings }>
   */
  gap?: Gap | ResponsiveGap | Spacings;
  /**
   * Specifies what amount of space inside the flex container the item should take up.
   * Can be a [responsive prop](/docs/responsive-props).
   *
   * *Note: Cannot be used with `flexMode` prop.*
   *
   * *Note: only works for nested `<Box />`*
   * @type ResponsiveObject<number | string>
   */
  grow?: ResponsiveObject<number | string> | number | string;
  /**
   * Specifies the height of the box. Can be a [responsive prop](/docs/responsive-props).
   *
   * *Note: Cannot be used with fill="vertical" or fillViewport="vertical"*
   * @type ResponsiveObject<string>
   */
  height?: ResponsiveObject<string> | string;
  /**
   * Make Layout behave like an inline element but still lay out its content according to flexbox model.
   * Can be a [responsive prop](/docs/responsive-props).
   * @type ResponsiveObject<boolean>
   */
  inline?: ResponsiveObject<boolean> | boolean;
  /**
   * Specifies how the browser distributes space between and around
   * items along the main axis of their container. Can be a [responsive prop](/docs/responsive-props).
   * @type ResponsiveObject<'center' | 'flex-end' | 'flex-start' | 'space-around' | 'space-between' | 'space-evenly'>
   */
  justifyContent?: JustifyContentType | ResponsiveObject<JustifyContentType>;
  /**
   * Specifies the order used to lay out an item in its container. Items within the same
   * container are rendered in ascending order according to their order values.
   * Elements with the _same_ order value are rendered in the order in which they appear in code
   *
   * *Note: only works for nested `<Box />`*
   */
  order?: number | 'inherit' | 'initial' | 'unset';
  // This will inherit the jsdoc block from PolymorphicComponent
  renderAs?: ElementType;
  /**
   * Specifies how items will shrink inside the container when the default size of
   * items is larger than their container. Can be a [responsive prop](/docs/responsive-props).
   *
   * *Note: Cannot be used with `flexMode` prop.*
   *
   * *Note: only works for nested `<Box />`*
   * @type ResponsiveObject<number>
   */
  shrink?: ResponsiveObject<number> | number;
  /**
   * Specifies whether items are forced into a single line or can be wrapped onto multiple lines.
   * Can be a [responsive prop](/docs/responsive-props).
   * @type ResponsiveObject<boolean>
   */
  wrapItems?: ResponsiveObject<boolean> | boolean;
  /**
   * Specifies the width of the box. Can be a [responsive prop](/docs/responsive-props).
   *
   * *Note: Cannot be used with fill="horizontal" or fillViewport="horizontal"*
   * @type ResponsiveObject<string>
   */
  width?: ResponsiveObject<string> | string;
};

const BoxComponent = forwardRef<HTMLDivElement, BoxProps>(
  (
    {
      alignContent = 'stretch',
      alignItems = 'flex-start',
      alignSelf,
      basis,
      children,
      direction = 'row',
      fill = false,
      fillViewport = false,
      gap,
      grow,
      height,
      inline = false,
      justifyContent = 'flex-start',
      order,
      renderAs,
      shrink,
      width,
      wrapItems = false,
      flexMode,
      ...props
    }: BoxProps,
    ref,
  ): JSX.Element => {
    const responsiveAlignContent = useResponsiveProp(alignContent);
    const responsiveAlignItems = useResponsiveProp(alignItems);
    const responsiveAlignSelf = useResponsiveProp(alignSelf);
    const responsiveBasis = useResponsiveProp(basis);
    const responsiveDirection = useResponsiveProp(direction);
    const responsiveFill = useResponsiveProp(fill);
    const responsiveFlexMode = useResponsiveProp(flexMode);
    const responsiveGap = useResponsiveProp(gap);
    const responsiveGrow = useResponsiveProp(grow);
    const responsiveHeight = useResponsiveProp(height);
    const responsiveInline = useResponsiveProp(inline);
    const responsiveJustifyContent = useResponsiveProp(justifyContent);
    const responsiveShrink = useResponsiveProp(shrink);
    const responsiveWidth = useResponsiveProp(width);
    const responsiveWrapItems = useResponsiveProp(wrapItems);

    // validate incompatible prop combinations
    if (isNotUndefined(responsiveHeight)) {
      if (responsiveFill === true || responsiveFill === 'vertical') {
        throw new Error('Cannot use height prop with fill="vertical" or fill={true}');
      }
      if (fillViewport === true || fillViewport === 'vertical') {
        throw new Error(
          'Cannot use height prop with fillViewport="vertical" or fillViewport={true}',
        );
      }
    }

    if (isNotUndefined(responsiveWidth)) {
      if (responsiveFill === true || responsiveFill === 'horizontal') {
        throw new Error('Cannot use width prop with fill="horizontal" or fill={true}');
      }
      if (fillViewport === true || fillViewport === 'horizontal') {
        throw new Error(
          'Cannot use width prop with fillViewport="horizontal" or fillViewport={true}',
        );
      }
    }

    return (
      <StyledBoxComponent
        ref={ref}
        $alignContent={responsiveAlignContent}
        $alignItems={responsiveAlignItems}
        $alignSelf={responsiveAlignSelf}
        $basis={responsiveBasis}
        $fillBox={responsiveFill}
        $fillViewport={fillViewport}
        $flexDirection={responsiveDirection}
        $flexMode={responsiveFlexMode}
        $gap={responsiveGap}
        $grow={responsiveGrow}
        $height={responsiveHeight}
        $inline={responsiveInline}
        $justifyContent={responsiveJustifyContent}
        $order={order}
        $shrink={responsiveShrink}
        $width={responsiveWidth}
        $wrapItems={responsiveWrapItems}
        as={renderAs ?? DEFAULT_ELEMENT}
        {...props}
      >
        {children}
      </StyledBoxComponent>
    );
  },
);

BoxComponent.displayName = 'Box';

/**
 * Box is a layout container using [CSS Flexbox](https://developer.mozilla.org/en-US/docs/Learn/CSS/CSS_layout/Flexbox).
 */
// @ts-expect-error makePolymorphic is causing issues with types in R19
export const Box = makePolymorphic<typeof DEFAULT_ELEMENT, BoxProps>(BoxComponent);
